import type { C1_Opening } from "./C1_Opening";
import type { C1_Room } from "./C1_Room";
import type { C1_OpeningData } from "../types/C1_ModelTypes";
import type { C1_LegacyEloItemTreeData } from "../types/C1_EloTypes";
import { cloneEloTreeWithFreshIds } from "./C1_cloneEloTree";

export type C1_OpeningClipboard = {
  roomId: string;
  data: C1_OpeningData;
  /** Whether the source wall ran horizontally; determines which axis the paste offset follows. */
  horizontal: boolean;
  /** Opening center as a fraction of the source room's bounding rect, independent of room shape. */
  relativePosition: { x: number; y: number };
  eloTree?: C1_LegacyEloItemTreeData;
};

export function copyOpening(opening: C1_Opening): C1_OpeningClipboard {
  const room = opening.room;
  if (!room) throw new Error("Cannot copy an opening without a room.");
  const rect = room.rect();
  const width = rect.x2 - rect.x1;
  const height = rect.y2 - rect.y1;
  const center = opening.openingCenter();
  return structuredClone({
    roomId: room.id,
    data: { ...opening.toJSON(), masterIndex: undefined },
    horizontal: opening.horizontal,
    relativePosition: {
      x: width > 0 ? (center.x - rect.x1) / width : 0.5,
      y: height > 0 ? (center.y - rect.y1) / height : 0.5,
    },
    eloTree: opening.eloTree,
  });
}

/** Rebuilds an independent opening on the nearest wall of the target room, mirroring manual placement. */
export function pasteOpening(clipboard: C1_OpeningClipboard, room: C1_Room, placement: C1_Opening): C1_Opening {
  const { data, eloTree } = structuredClone(clipboard);
  const rect = room.rect();
  const width = rect.x2 - rect.x1;
  const height = rect.y2 - rect.y1;
  if (width <= 0 || height <= 0) throw new Error("Cannot paste an opening into a room without usable dimensions.");
  if (!placement.data._wallId) throw new Error("Cannot paste an opening without a usable wall.");
  const opening = room.addOpening({
    ...data,
    masterIndex: undefined,
    roomId: room.id,
    side: placement.side,
    _wallId: placement.data._wallId,
    offset: placement.offset,
    mode: "manual",
    reflect: placement.reflect,
  });
  if (eloTree) opening.setEloTree(cloneEloTreeWithFreshIds(eloTree));
  return opening;
}
